import {
  Component,
  Input,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  AfterViewInit,
  OnDestroy,
  ContentChildren,
  QueryList,
} from "@angular/core";
import { trigger, state, style, transition, animate } from "@angular/animations";
import { AnimationStates } from "../types/tooltips.types";
import { Overlay, OverlayPositionBuilder, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { DsOptionComponent } from "../ds-selector-components/ds-option/ds-option.component";
import { DsOptionGroupComponent } from "../ds-selector-components/ds-option-group/ds-option-group.component";
import { DsOptionService } from "../../services/ds-option.service";
import { isEqual } from "lodash-es";

@Component({
  selector: "ds-autocomplete",
  templateUrl: "./ds-autocomplete.component.html",
  styleUrls: ["./ds-autocomplete.component.scss"],
  animations: [
    trigger("fadeInOut", [
      state(
        "hidden",
        style({
          opacity: 0,
          transform: "scale(0.8) translateY(-8px)",
        })
      ),
      state(
        "visible",
        style({
          opacity: 1,
          transform: "scale(1) translateY(0)",
        })
      ),
      transition("hidden => visible", [animate("300ms cubic-bezier(0.25, 0.8, 0.25, 1)")]),
      transition("visible => hidden", [animate("300ms cubic-bezier(0.4, 0, 0.2, 1)")]),
    ]),
  ],
})
export class DsAutocompleteComponent implements AfterViewInit, OnDestroy {
  @ViewChild("autocomplete") autocomplete!: TemplateRef<any>;
  @ContentChildren(DsOptionComponent, { descendants: true }) options!: QueryList<DsOptionComponent>;
  @ContentChildren(DsOptionGroupComponent, { descendants: true }) optionGroups!: QueryList<DsOptionGroupComponent>;
  @Input() message = "";
  @Input() elementRef!: HTMLElement;
  animationState: AnimationStates = "hidden";
  value: any = null;

  overlayRef: OverlayRef | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private highlightedIndex = -1;

  constructor(
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private vcr: ViewContainerRef,
    private dsOptionService: DsOptionService
  ) {}

  private onDocumentClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (this.elementRef && this.elementRef.contains(target)) {
      return;
    }
    if (this.overlayRef) {
      const overlayElement = this.overlayRef.overlayElement;
      if (overlayElement && overlayElement.contains(target)) {
        return;
      }
    }
    this.hideOptions();
  };

  private startResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      if (this.overlayRef) {
        this.overlayRef.updateSize({ width: this.elementRef.offsetWidth });
      }
    });
    this.resizeObserver.observe(this.elementRef);
  }

  private stopResizeObserver(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  ngAfterViewInit(): void {
    if (this.elementRef) {
      (this.elementRef as HTMLInputElement).autocomplete = "off";
      this.elementRef.addEventListener("focus", this.showOptions);
      this.elementRef.addEventListener("input", this.inputChanged);
      this.elementRef.addEventListener("pointerdown", this.showOptions);
      this.elementRef.addEventListener("keydown", this.onKeyDown);
    }
    this.setupOptionListeners();
  }

  ngOnDestroy(): void {
    document.removeEventListener("click", this.onDocumentClick);
    this.stopResizeObserver();
    this.hideOptions();
    if (this.elementRef) {
      this.elementRef.removeEventListener("focus", this.showOptions);
      this.elementRef.removeEventListener("input", this.inputChanged);
      this.elementRef.removeEventListener("pointerdown", this.showOptions);
      this.elementRef.removeEventListener("keydown", this.onKeyDown);
    }
  }

  inputChanged = () => {
    const lowerCaseValue = (this.elementRef as HTMLInputElement).value.toLowerCase();
    if (lowerCaseValue) {
      this.options.forEach((opt) => {
        if (opt.alwaysVisible) {
          opt.hidden = false;
          return;
        }
        const lowerCaseDisplayText = opt.displayText.toLowerCase();
        opt.hidden = !lowerCaseDisplayText.includes(lowerCaseValue);
      });
    } else {
      this.options.forEach((opt) => {
        opt.hidden = false;
        opt.selected = false;
      });
    }
    this.updateGroupsVisibility();
    this.resetHighlight();
    if (this.areAllOptionsHidden()) {
      this.hideOptions();
    } else {
      this.showOptions();
    }
  };

  private onKeyDown = (event: KeyboardEvent) => {
    if (!this.overlayRef) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        this.showOptions();
      }
      return;
    }

    const visibleOptions = this.getVisibleOptions();
    if (visibleOptions.length === 0) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.highlightedIndex = this.highlightedIndex < visibleOptions.length - 1 ? this.highlightedIndex + 1 : 0;
        this.updateOptionsHighlight();
        this.scrollToHighlightedOption();
        break;
      case "ArrowUp":
        event.preventDefault();
        this.highlightedIndex = this.highlightedIndex > 0 ? this.highlightedIndex - 1 : visibleOptions.length - 1;
        this.updateOptionsHighlight();
        this.scrollToHighlightedOption();
        break;
      case "Enter": {
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < visibleOptions.length) {
          const selectedOption = visibleOptions[this.highlightedIndex];
          selectedOption.optionSelected.emit(selectedOption);
        }
        break;
      }
      case "Escape":
        event.preventDefault();
        this.hideOptions();
        break;
    }
  };

  private getVisibleOptions(): DsOptionComponent[] {
    return this.dsOptionService.getEnabledOptions(this.options).filter((opt) => !opt.hidden);
  }

  private updateOptionsHighlight(): void {
    const visibleOptions = this.getVisibleOptions();
    this.options.forEach((option) => {
      const visibleIndex = visibleOptions.indexOf(option);
      option.highlighted = visibleIndex === this.highlightedIndex;
    });
  }

  private scrollToHighlightedOption(): void {
    const visibleOptions = this.getVisibleOptions();
    if (this.highlightedIndex >= 0 && this.highlightedIndex < visibleOptions.length) {
      const highlightedOption = visibleOptions[this.highlightedIndex];
      if (highlightedOption?.elementRefInstance) {
        highlightedOption.elementRefInstance.nativeElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }

  private resetHighlight(): void {
    this.highlightedIndex = -1;
    this.updateOptionsHighlight();
  }

  private areAllOptionsHidden(): boolean {
    return this.options && this.options.length > 0 && this.options.toArray().every((opt) => opt.hidden);
  }

  private updateGroupsVisibility(): void {
    if (this.optionGroups) {
      this.optionGroups.forEach((group) => group.checkVisibility());
    }
  }

  showOptions = () => {
    if (this.overlayRef || this.areAllOptionsHidden()) return;

    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions(this.getPositionConfig())
      .withPush(false)
      .withFlexibleDimensions(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
      hasBackdrop: false,
      width: this.elementRef.offsetWidth,
    });
    this.overlayRef.detachments().subscribe(() => {
      this.onOverlayClosed();
    });

    const portal = new TemplatePortal(this.autocomplete, this.vcr);
    this.overlayRef.attach(portal);
    this.startResizeObserver();
    setTimeout(() => {
      document.addEventListener("click", this.onDocumentClick);
    });

    setTimeout(() => {
      this.animationState = "visible";
    });
  };

  private onOverlayClosed() {
    document.removeEventListener("click", this.onDocumentClick);
    this.stopResizeObserver();
    this.animationState = "hidden";
    this.overlayRef = null;
  }

  hideOptions = () => {
    if (!this.overlayRef) return;
    document.removeEventListener("click", this.onDocumentClick);
    this.stopResizeObserver();
    this.resetHighlight();

    this.animationState = "hidden";
    setTimeout(() => {
      this.overlayRef?.dispose();
      this.overlayRef = null;
    }, 300);
  };

  private getPositionConfig(): any[] {
    const positionAbove = {
      originX: "start" as const,
      originY: "top" as const,
      overlayX: "start" as const,
      overlayY: "bottom" as const,
    };
    const positionBelow = {
      originX: "start" as const,
      originY: "bottom" as const,
      overlayX: "start" as const,
      overlayY: "top" as const,
    };
    return [positionBelow, positionAbove];
  }

  private setupOptionListeners() {
    this.options.forEach((option) => {
      option.dropdown = true;
      option.optionSelected.subscribe((selectedOption: DsOptionComponent) => {
        this.updateOptionsSelection(selectedOption);
      });
    });
  }

  private updateOptionsSelection(option: DsOptionComponent) {
    if (!this.options) return;

    this.options.forEach((opt) => {
      opt.selected = isEqual(option.value, opt.value);
    });

    if (this.elementRef && this.elementRef instanceof HTMLInputElement) {
      this.elementRef.value = option.value;
    }

    this.hideOptions();
  }
}
